<script setup lang="ts">
  import { ROUTES } from '@/router/routes'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import type { PaginatedResponse } from '@/components/admin/PaginatedTable.vue'
  import { type SuperUserUser as User } from '@/types/generated/types.gen'
  import { UsersService } from '@/types/generated/services.gen'
  import type { FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import { TableCell, TableRow } from '@/components/ui/table'

  const fetchUsers = async (options?: FetchDataOptions): Promise<PaginatedResponse<unknown>> => {
    const response = await UsersService.usersList({
      limit: options?.limit,
      offset: options?.offset
    })
    return {
      results: response.results as unknown[],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count
    }
  }
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Users', to: ROUTES.ADMIN.USERS.USER_LIST }
          ]"
        />
      </div>
      <div
        class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm
          dark:border-neutral-700 dark:bg-neutral-800"
      >
        <PaginatedTableView
          :fetchData="fetchUsers"
          searchable-fields="UUID, email, first name, last name"
        >
          <template #table-head>
            <TableCell>UUID</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>First Name</TableCell>
            <TableCell>Last Name</TableCell>
            <TableCell>Organization</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Actions</TableCell>
          </template>
          <template #table-row="{ items }">
            <TableRow
              v-for="user in items"
              :key="user.uuid"
            >
              <UniqueTableDataCell :truncate="true">
                <router-link
                  :to="ROUTES.ADMIN.USERS.USER_DETAIL.replace(':uuid', (user as User).uuid)"
                >
                  {{ (user as User).uuid }}
                </router-link>
              </UniqueTableDataCell>
              <TableCell>
                {{ (user as User).email }}
              </TableCell>
              <TableCell>
                {{ (user as User).first_name }}
              </TableCell>
              <TableCell>
                {{ (user as User).last_name }}
              </TableCell>
              <TableCell>
                {{ (user as User).org?.name || 'N/A' }}
              </TableCell>
              <TableCell>
                {{ (user as User).role }}
              </TableCell>
              <TableCell>
                <TableOptionsButton
                  :options="[
                    {
                      label: 'Edit',
                      to: ROUTES.ADMIN.USERS.USER_DETAIL.replace(':uuid', (user as User).uuid)
                    }
                  ]"
                />
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>
      </div>
    </div>
  </div>
</template>
